/**
 * ProductionForecast — Tier-1 Pro tool #3.
 * Monthly calendar that lets an operator drop scheduled batches (recipe
 * + start date + tray count) and see cost + output rollups per month.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, Plus, Loader2, Trash2,
} from 'lucide-react';
import ProTierGate from '../components/tools/ProTierGate';
import PageHero from '../components/PageHero';

const monthLabel = (d) => d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
const isoMonth   = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const isoDate    = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const buildMonthGrid = (year, month) => {
  // month is 0-based; grid starts on the previous Sunday.
  const first = new Date(year, month, 1);
  const startOffset = first.getDay();
  const gridStart = new Date(year, month, 1 - startOffset);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
};

const Inner = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [items, setItems] = useState([]);
  const [rollup, setRollup] = useState({ total_cost: 0, total_output_kg: 0, total_hours: 0 });
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null);   // {date: ISO}
  const [recipeId, setRecipeId] = useState('');
  const [trays, setTrays] = useState('1');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const monthStr = isoMonth(cursor);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tools/production-forecast`, { params: { month: monthStr } });
      setItems(data.items || []);
      setRollup({ total_cost: data.total_cost, total_output_kg: data.total_output_kg, total_hours: data.total_hours });
    } catch (err) {
      // silent
    } finally { setLoading(false); }
  };

  const loadRecipes = async () => {
    try {
      const { data } = await axios.get(`${API}/tools/recipe-builder`);
      setRecipes(Array.isArray(data) ? data : []);
    } catch (err) { /* silent */ }
  };

  useEffect(() => { load(); }, [monthStr]); // eslint-disable-line
  useEffect(() => { loadRecipes(); }, []); // eslint-disable-line

  // Honor `?recipe_id=…` deep link from Recipe Builder: once recipes
  // list has loaded, auto-open the schedule dialog with that recipe
  // pre-selected for today's date so the operator can just pick a day
  // and confirm.
  useEffect(() => {
    const rid = params.get('recipe_id');
    if (!rid || recipes.length === 0) return;
    if (!recipes.find(r => r.id === rid)) return;
    const today = isoDate(new Date());
    setDialog({ date: today });
    setRecipeId(rid);
    setTrays('1');
    setNotes('');
  }, [params, recipes]);

  const grid = useMemo(() => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const itemsByDate = useMemo(() => {
    const map = {};
    items.forEach(it => { (map[it.start_date] ||= []).push(it); });
    return map;
  }, [items]);

  const openDialog = (dateIso) => {
    setDialog({ date: dateIso });
    setRecipeId(recipes[0]?.id || '');
    setTrays('1');
    setNotes('');
  };

  const saveBatch = async () => {
    if (!recipeId) return toast.error('Pick a recipe');
    setSaving(true);
    try {
      await axios.post(`${API}/tools/production-forecast`, {
        recipe_id: recipeId,
        start_date: dialog.date,
        trays: Number(trays) || 1,
        notes: notes.trim() || null,
      });
      toast.success('Batch scheduled');
      setDialog(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Schedule failed');
    } finally { setSaving(false); }
  };

  const removeBatch = async (scheduleId) => {
    if (!window.confirm('Remove this scheduled batch?')) return;
    try {
      await axios.delete(`${API}/tools/production-forecast/${scheduleId}`);
      load();
    } catch { toast.error('Delete failed'); }
  };

  const nav = (delta) => setCursor(c => new Date(c.getFullYear(), c.getMonth() + delta, 1));

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="production-forecast-inner">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="pf-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
        </Button>
        <div className="text-[10px] uppercase tracking-widest font-extrabold text-primary">Tier-1 · Pro Tool</div>
      </div>
      <PageHero
        icon={<CalendarDays className="w-8 h-8" />}
        eyebrow="Production Forecast"
        title="Plan the month, know the numbers"
        subtitle="Schedule Recipe Builder batches on the calendar. We roll up cost, output, and freeze-dryer hours so you can see what next month looks like before you commit."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl p-3 bg-primary/10 border border-primary/30" data-testid="pf-rollup-cost">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Projected cost</div>
          <div className="mt-1 text-2xl font-extrabold text-foreground">${rollup.total_cost.toFixed(2)}</div>
        </div>
        <div className="rounded-xl p-3 bg-emerald-50 border border-emerald-200" data-testid="pf-rollup-output">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Projected output</div>
          <div className="mt-1 text-2xl font-extrabold text-foreground">{rollup.total_output_kg.toFixed(2)} kg</div>
        </div>
        <div className="rounded-xl p-3 bg-amber-50 border border-amber-200" data-testid="pf-rollup-hours">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Machine hours</div>
          <div className="mt-1 text-2xl font-extrabold text-foreground">{rollup.total_hours.toFixed(0)} h</div>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => nav(-1)} data-testid="pf-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
            <CardTitle className="text-base" data-testid="pf-month-label">{monthLabel(cursor)}</CardTitle>
            <Button variant="ghost" size="icon" onClick={() => nav(1)} data-testid="pf-next-month"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} className="text-center py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="pf-calendar-grid">
            {grid.map((d) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const dIso = isoDate(d);
              const list = itemsByDate[dIso] || [];
              return (
                <div
                  key={dIso}
                  className={`min-h-[86px] rounded-lg border p-1 ${inMonth ? 'bg-background' : 'bg-muted/20 text-muted-foreground'} border-border`}
                  data-testid={`pf-day-${dIso}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] font-bold">{d.getDate()}</div>
                    {inMonth && (
                      <button onClick={() => openDialog(dIso)} className="text-primary hover:text-primary/70" data-testid={`pf-add-${dIso}`}>
                        <Plus className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {list.map(it => (
                      <div key={it.id} className="text-[10px] rounded bg-primary/10 border border-primary/30 px-1 py-0.5 truncate" title={`${it.recipe_name} · $${it.total_cost}`} data-testid={`pf-item-${it.id}`}>
                        <div className="font-bold truncate">{it.recipe_name}</div>
                        <div className="flex items-center justify-between">
                          <span>${it.total_cost}</span>
                          <button onClick={() => removeBatch(it.id)} className="text-rose-600 hover:text-rose-700" data-testid={`pf-remove-${it.id}`}><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {dialog && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50" onClick={() => setDialog(null)} data-testid="pf-schedule-dialog">
          <div className="bg-background rounded-2xl p-5 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Schedule batch on {dialog.date}</div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Recipe</label>
              <select
                className="mt-1 w-full h-10 rounded-md border border-input bg-background px-2 text-sm"
                value={recipeId}
                onChange={(e) => setRecipeId(e.target.value)}
                data-testid="pf-recipe-select"
              >
                {recipes.length === 0 && <option value="">No saved recipes</option>}
                {recipes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Trays</label>
              <Input type="number" min="1" max="20" value={trays} onChange={(e) => setTrays(e.target.value)} className="mt-1" data-testid="pf-trays-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Notes</label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="pf-notes-input" />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setDialog(null)} data-testid="pf-cancel-btn">Cancel</Button>
              <Button className="rounded-full bg-primary text-white font-extrabold" onClick={saveBatch} disabled={saving || !recipeId} data-testid="pf-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Schedule'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProductionForecast = () => (
  <ProTierGate toolName="Production Forecast" tagline="Plan a month of freeze-dryer output with cost + throughput rollups." data_testid_prefix="production-forecast-gate">
    <Inner />
  </ProTierGate>
);
export default ProductionForecast;
